import React from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme } from '../../../../ui';
import Icon from '../../../../ui/Icon';
import { useSettingsObject } from '../../../settings/hooks';
import { getUserThemeColor, toRgba } from '../../../../ui/theme/colors';

const WidgetContainer = styled(Flex)`
  background: ${theme.colors.NEU_0};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.04)};
  width: 100%;
`;

const WidgetHeader = styled(Flex)`
  margin-bottom: 20px;
`;

const EvaluationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const EvaluationItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${theme.colors.NEU_100};
  cursor: pointer;
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const IconCircleWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const IconCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$bgColor}15;
`;

const PriorityIndicator = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid ${theme.colors.NEU_0};
  background: ${(props) => {
    switch (props.$priority) {
      case 'high':
        return theme.colors.ERROR_RED;
      case 'medium':
        return theme.colors.SECONDARY;
      default:
        return theme.colors.SUCCESS_GREEN;
    }
  }};
`;

const EmptyState = styled(Flex)`
  padding: 32px 20px;
  gap: 16px;
`;

/**
 * PendingEvaluationsWidget Component
 *
 * Shows tasks/submissions awaiting teacher feedback with:
 * - Priority indicator
 * - Student name and course
 * - Task title and type
 * - Submission date
 */
const PendingEvaluationsWidget = ({ evaluations = [] }) => {
  const settings = useSettingsObject();
  const userTheme = settings?.theme;
  const brandColor = userTheme?.color ? getUserThemeColor(userTheme?.color) : theme.colors.PRIMARY;

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getTaskIcon = (taskType) => {
    switch (taskType) {
      case 'file':
        return 'Upload';
      case 'quiz':
        return 'Quiz';
      default:
        return 'Task';
    }
  };

  if (!evaluations.length) {
    return (
      <WidgetContainer direction='column' align='flex-start'>
        <WidgetHeader align='center' justify='space-between' width='100%'>
          <Flex direction='column' align='flex-start'>
            <Typo.ContentSmallHeader $textAlign='left' color={theme.colors.NEU_PRIMARY}>
              Pending Evaluations
            </Typo.ContentSmallHeader>
            <Typo.Info color={theme.colors.NEU_600}>Student submissions to review</Typo.Info>
          </Flex>
          <Icon.Task size={'medium'} color={brandColor} />
        </WidgetHeader>
        <EmptyState direction='column' align='center' justify='center' width='100%'>
          <Icon.Check size={'large'} color={theme.colors.SUCCESS_GREEN} />
          <Typo.MediumText color={theme.colors.NEU_600}>All caught up! No pending evaluations.</Typo.MediumText>
        </EmptyState>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer direction='column' align='flex-start'>
      <WidgetHeader align='center' justify='space-between' width='100%'>
        <Flex direction='column' align='flex-start'>
          <Typo.ContentSmallHeader $textAlign='left' color={theme.colors.NEU_PRIMARY}>
            Pending Evaluations
          </Typo.ContentSmallHeader>
          <Typo.Info color={theme.colors.NEU_600}>
            {evaluations.length} submission{evaluations.length !== 1 ? 's' : ''} to review
          </Typo.Info>
        </Flex>
        <Icon.Task size={'medium'} color={brandColor} />
      </WidgetHeader>

      <EvaluationsList>
        {evaluations.map((evaluation) => {
          const TaskIcon = Icon[getTaskIcon(evaluation.taskType)];
          return (
            <EvaluationItem key={evaluation.id}>
              <IconCircleWrapper>
                <IconCircle $bgColor={brandColor}>{TaskIcon && <TaskIcon size={'small'} color={brandColor} />}</IconCircle>
                <PriorityIndicator $priority={evaluation.priority} />
              </IconCircleWrapper>
              <Flex direction='column' align='flex-start' style={{ flex: 1 }}>
                <Typo.TextTitle fontWeight='600'>{evaluation.studentName}</Typo.TextTitle>
                <Typo.SmallText color={theme.colors.NEU_600}>{evaluation.taskTitle}</Typo.SmallText>
                <Typo.Info color={theme.colors.NEU_600}>{evaluation.courseTitle}</Typo.Info>
              </Flex>
              <Typo.Info color={theme.colors.NEU_600}>{formatRelativeTime(evaluation.submittedDate)}</Typo.Info>
            </EvaluationItem>
          );
        })}
      </EvaluationsList>
    </WidgetContainer>
  );
};

export default PendingEvaluationsWidget;
